import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INIT = {
  company: "",
  contact_name: "",
  email: "",
  phone: "",
  annual_turnover: "",
  monthly_invoices: "",
  main_clients: "",
  payment_terms: "",
  funding_requirement: "",
  permission_contact: false,
};

export default function Finance() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [e.target.name]: v }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      toast.error("Please provide company, name and email.");
      return;
    }
    if (!form.permission_contact) {
      toast.error("Please provide permission to be contacted regarding finance.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/finance-enquiries`, form);
      setDone(true);
      setForm(INIT);
      toast.success("Enquiry received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteShell>
      <PageHero
        overline="Finance Support"
        title="Don't let payment terms limit your growth."
        kicker="Winning a bigger contract is only valuable if you have the cash flow to deliver it. Verified Workforce helps eligible Delivery Partners explore finance solutions designed around the realities of construction."
        image="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <a href="#enquire" className="vw-btn vw-btn--primary" data-testid="finance-enquire-cta">
            Explore Finance Support
            <ArrowUpRight size={16} />
          </a>
          <Link to="/readiness" className="vw-btn vw-btn--gold">
            Check Your Contract Readiness
          </Link>
        </div>
      </PageHero>

      {/* The problem */}
      <section className="vw-section bg-[var(--vw-paper)]" data-testid="finance-problem-section">
        <div className="vw-container">
          <Reveal>
            <div className="vw-overline mb-4">The Cash Flow Reality</div>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight max-w-3xl leading-[1.05]">
              Construction cash flow in six steps.
            </h2>
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-5 gap-5 items-stretch">
            {[
              ["WORK COMPLETED", "You deliver the works on site."],
              ["INVOICE / APPLICATION", "Raised in line with the payment schedule."],
              ["30 – 90 DAY TERMS", "Client payment terms kick in."],
              ["MEANWHILE…", "Wages, materials, plant, VAT, next project."],
              ["CASH GAP", "A funding gap that constrains growth."],
            ].map(([title, body], i) => (
              <Reveal key={title} delay={i * 0.07}>
                <div className={`relative p-7 h-full border ${i === 4 ? "bg-[var(--vw-navy)] text-white border-[var(--vw-gold)]" : "bg-white border-[var(--vw-line)]"}`}>
                  <div className={`font-mono text-[11px] tracking-[0.22em] uppercase ${i === 4 ? "text-[var(--vw-gold)]" : "text-[var(--vw-blue)]"}`}>
                    Step 0{i + 1}
                  </div>
                  <div className={`mt-4 font-display text-lg font-semibold ${i === 4 ? "text-white" : "text-[var(--vw-navy)]"}`}>
                    {title}
                  </div>
                  <p className={`mt-3 text-sm leading-relaxed ${i === 4 ? "text-white/70" : "text-[var(--vw-muted)]"}`}>
                    {body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-[var(--vw-muted)] leading-relaxed">
            Verified Workforce helps eligible Delivery Partners explore finance solutions
            that can bridge this gap — so a bigger contract is a bigger opportunity, not a
            bigger problem.
          </p>
        </div>
      </section>

      {/* Apollo */}
      <section className="vw-section bg-white" data-testid="apollo-section">
        <div className="vw-container">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="vw-overline mb-4">Specialist Finance Partnership</div>
                <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                  Finance support through Apollo Business Finance.
                </h2>
                <p className="mt-6 text-[var(--vw-muted)] leading-relaxed max-w-2xl">
                  Verified Workforce works with Apollo Business Finance to help eligible
                  contractors explore invoice finance solutions designed around the
                  realities of construction.
                </p>
                <ul className="mt-6 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-[var(--vw-charcoal)]">
                  {["CIS","Retentions","Applications for payment","Staged invoicing","Main-contractor payment cycles"].map((x) => (
                    <li key={x} className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[var(--vw-blue)] mt-0.5"/>{x}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.08}>
                <div className="bg-[var(--vw-navy)] text-white p-8 lg:p-10 border border-white/10">
                  <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-gold)] uppercase">Working With</div>
                  <div className="mt-2 font-display text-3xl font-semibold">Apollo Business Finance</div>
                  <div className="mt-6 h-px bg-white/10" />
                  <p className="mt-5 text-sm text-white/70 leading-relaxed">
                    Apollo specialises in construction and subcontractor finance and
                    understands the specific commercial patterns of the industry.
                  </p>
                  <p className="mt-5 text-xs text-white/50 leading-relaxed">
                    Finance is provided by third-party finance providers and remains
                    subject to eligibility, approval, terms, fees and individual
                    circumstances. Verified Workforce does not provide lending.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* How invoice finance works */}
          <div className="mt-20">
            <Reveal>
              <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[var(--vw-navy)] tracking-tight">
                How invoice finance can work.
              </h3>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                ["COMPLETE THE WORK", "Complete the works and raise your invoice/application in the normal way."],
                ["ACCESS CASH EARLIER", "Subject to approval and eligibility, Apollo can advance up to 90% of qualifying invoice value, potentially within 24 hours of approval."],
                ["KEEP DELIVERING", "Use improved cash flow to support wages, suppliers and future projects rather than waiting 30, 60 or 90 days for payment."],
                ["BALANCE PAID", "When the client settles the invoice, the remaining balance is dealt with in accordance with Apollo's agreed facility terms and fees."],
              ].map(([t,b], i) => (
                <Reveal key={t} delay={i*0.06}>
                  <div className="vw-card h-full p-7">
                    <div className="font-mono text-[11px] tracking-[0.22em] text-[var(--vw-blue)] uppercase">
                      Step 0{i+1}
                    </div>
                    <div className="mt-3 font-display text-lg font-semibold text-[var(--vw-navy)]">{t}</div>
                    <p className="mt-3 text-sm text-[var(--vw-muted)] leading-relaxed">{b}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Example */}
          <div className="mt-16">
            <Reveal>
              <div className="relative overflow-hidden p-10 lg:p-14 border border-[var(--vw-line)] bg-[var(--vw-cream)]">
                <div className="absolute top-6 right-6 vw-badge vw-badge--gold">Illustrative Example</div>
                <div className="vw-overline mb-4">Example</div>
                <h3 className="font-display text-2xl lg:text-4xl font-semibold text-[var(--vw-navy)] max-w-3xl leading-[1.05]">
                  A contractor completes <span className="text-[var(--vw-gold)]">£50,000</span> of qualifying invoiced work.
                </h3>
                <p className="mt-5 text-[var(--vw-charcoal)] max-w-3xl leading-relaxed">
                  Instead of potentially waiting several weeks for payment, invoice finance
                  may allow them to access a significant proportion of that invoice earlier
                  — subject to approval. This can help fund:
                </p>
                <ul className="mt-5 grid sm:grid-cols-3 gap-y-2 gap-x-6 text-sm text-[var(--vw-charcoal)] max-w-3xl">
                  {["Payroll","Materials","Suppliers","Plant","Mobilisation","Additional contracts"].map((x) => (
                    <li key={x} className="flex gap-2"><CheckCircle2 size={16} className="text-[var(--vw-blue)] mt-0.5"/>{x}</li>
                  ))}
                </ul>
                <p className="mt-6 text-[11px] text-[var(--vw-muted)] max-w-3xl">
                  Figures are illustrative only and do not represent a quotation, offer or
                  commitment. Any advance is subject to eligibility, approval, terms and fees.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquire" className="vw-section bg-[var(--vw-paper)]" data-testid="finance-enquire-section">
        <div className="vw-container grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="vw-overline mb-4">Explore Finance Support</div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
                Tell us about your business.
              </h2>
              <p className="mt-6 text-[var(--vw-muted)] leading-relaxed">
                Share a few details and we'll follow up to discuss whether invoice finance
                through Apollo Business Finance could support your delivery.
              </p>
              <p className="mt-6 text-xs text-[var(--vw-muted)] leading-relaxed">
                Finance is provided by third-party finance providers and remains subject
                to eligibility, approval, terms and fees. Verified Workforce does not
                provide lending.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <div className="bg-white p-8 lg:p-10 border border-[var(--vw-line)]">
                {done ? (
                  <div className="text-center py-10" data-testid="finance-success">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[var(--vw-blue)]/10 text-[var(--vw-blue)] mb-6">
                      <CheckCircle2 size={30} />
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-[var(--vw-navy)]">Enquiry received</h3>
                    <p className="mt-3 text-[var(--vw-muted)]">A member of the team will be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="finance-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <F label="Company *" name="company" value={form.company} onChange={onChange} required testid="fin-company"/>
                      <F label="Contact Name *" name="contact_name" value={form.contact_name} onChange={onChange} required testid="fin-name"/>
                      <F label="Email *" type="email" name="email" value={form.email} onChange={onChange} required testid="fin-email"/>
                      <F label="Phone" name="phone" value={form.phone} onChange={onChange} testid="fin-phone"/>
                      <F label="Annual Turnover" name="annual_turnover" value={form.annual_turnover} onChange={onChange} testid="fin-turnover"/>
                      <F label="Typical Monthly Invoices" name="monthly_invoices" value={form.monthly_invoices} onChange={onChange} testid="fin-invoices"/>
                      <F label="Main Clients" name="main_clients" value={form.main_clients} onChange={onChange} testid="fin-clients"/>
                      <F label="Typical Payment Terms" name="payment_terms" value={form.payment_terms} onChange={onChange} placeholder="e.g. 30 days, 60 days" testid="fin-terms"/>
                    </div>
                    <div className="mt-5 vw-field">
                      <label htmlFor="fin-req">Funding Requirement</label>
                      <textarea id="fin-req" name="funding_requirement" rows={3} value={form.funding_requirement} onChange={onChange} data-testid="fin-requirement"/>
                    </div>
                    <label className="mt-5 flex items-start gap-3 cursor-pointer" data-testid="fin-permission">
                      <input type="checkbox" name="permission_contact" checked={form.permission_contact} onChange={onChange} className="mt-1 w-4 h-4 accent-[var(--vw-blue)]"/>
                      <span className="text-sm text-[var(--vw-muted)]">I give permission to be contacted by Verified Workforce and introduced to Apollo Business Finance regarding finance support.</span>
                    </label>
                    <button type="submit" disabled={loading} className="vw-btn vw-btn--primary mt-6" data-testid="finance-submit-btn">
                      {loading ? <><Loader2 size={16} className="animate-spin"/> Sending…</> : "Submit Enquiry"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function F({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}/>
    </div>
  );
}
